import React, { useState, useEffect } from 'react';
import { useBranding } from '../../hooks/useBranding';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Lock, Visibility, VisibilityOff } from '@mui/icons-material';
import axios from 'axios';

interface ResetPasswordFormData {
  password: string;
  confirmPassword: string;
}

const authFieldSx = {
  '& .MuiInputBase-root': {
    backgroundColor: 'var(--auth-field-background) !important',
  },
  '& .MuiInputBase-root:hover': {
    backgroundColor: 'var(--auth-field-background) !important',
  },
  '& .MuiInputBase-root.Mui-focused': {
    backgroundColor: 'var(--auth-field-background) !important',
  },
  '& .MuiInputBase-input': {
    backgroundColor: 'transparent !important',
  },
  '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active': {
    WebkitBoxShadow: '0 0 0 1000px var(--auth-field-background) inset !important',
    boxShadow: '0 0 0 1000px var(--auth-field-background) inset !important',
    WebkitTextFillColor: 'var(--text-primary) !important',
    transition: 'background-color 5000s ease-in-out 0s',
  }
};

export const ResetPasswordForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const branding = useBranding();
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ResetPasswordFormData>();

  const password = watch('password');

  useEffect(() => {
    if (!token || !email) {
      setError('Invalid or missing reset link. Please request a new password reset.');
    }
  }, [token, email]);

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token || !email) {
      setError('Invalid reset link');
      return;
    }

    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      const response = await axios.post('/api/auth/reset-password', {
        token,
        email,
        newPassword: data.password
      });

      setMessage('Password reset successful! Redirecting to login...');

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/auth');
      }, 2000);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to reset password. Please try again or request a new reset link.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="glassContainer auth-form-container" style={{ padding: '20px', maxWidth: '500px', maxHeight: '90vh', background:'radial-gradient(circle at center, rgba(181, 231, 255, 0.2), rgba(0, 0, 0, 0.63) 70%)', position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', overflowY: 'auto' }}>
      <div className="logobox glassBtn" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
        <img src={branding.logoUrl || '/logo192.png'} alt="Logo" className="logo" style={{ height: 40, marginRight: 8 }} />
        <div style={{ fontSize: '24px', fontWeight: 'bold', fontFamily: 'inherit' }}>
          <span style={{ color: '#3a9397' }}>{branding.part1}</span>
          <span style={{ color: '#ff9800' }}>{branding.part2}</span>
        </div>
      </div>

      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Lock sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="h5" gutterBottom>
          Reset Password
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Enter your new password below
        </Typography>
      </Box>

      {message && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!error && token && email && (
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <TextField
            fullWidth
            label="New Password"
            type={showPassword ? 'text' : 'password'}
            {...register('password', {
              required: 'Password is required',
              minLength: {
                value: 8,
                message: 'Password must be at least 8 characters',
              },
            })}
            error={!!errors.password}
            helperText={errors.password?.message}
            sx={{ ...authFieldSx, mb: 2 }}
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Confirm Password"
            type={showConfirmPassword ? 'text' : 'password'}
            {...register('confirmPassword', {
              required: 'Please confirm your password',
              validate: (value) =>
                value === password || 'Passwords do not match',
            })}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword?.message}
            sx={{ ...authFieldSx, mb: 3 }}
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{ mb: 2 }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
          </Button>
        </Box>
      )}

      <Typography align="center" variant="body2" className="secondary-text">
        Remember your password?{' '}
        <Button
          variant="text"
          onClick={() => navigate('/auth')}
          disabled={loading}
          sx={{ textTransform: 'none' }}
        >
          Sign in
        </Button>
      </Typography>
    </div>
  );
};